"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import BlogCard from "@/components/blog/BlogCard";
import { CreditCard, Briefcase, Shield, TrendingUp, Landmark, Compass, Wallet, PiggyBank, FileText, FolderOpen, Star } from "lucide-react";
import { SITE_AUTHOR } from "@/lib/constants/eeat";

/** Card-level projection passed from the server page — never the full posts. */
export interface BlogIndexCard {
  slug: string;
  title: string;
  summary: string;
  category: string;
  date: string;
  countryCode?: string;
  countryName?: string;
  images?: Array<{ url: string; alt: string }>;
  readTimeMin: number;
}

export default function BlogIndexClient({ posts }: { posts: BlogIndexCard[] }) {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const CATEGORY_ICONS: Record<string, React.ReactNode> = {
    "Credit & Debt": <CreditCard size={14} />,
    "Employment & Salary Benefits": <Briefcase size={14} />,
    Insurance: <Shield size={14} />,
    Investments: <TrendingUp size={14} />,
    Loans: <Landmark size={14} />,
    Retirement: <Compass size={14} />,
    "Retirement & Pensions": <Wallet size={14} />,
    Savings: <PiggyBank size={14} />,
    Tax: <FileText size={14} />,
    "Tax & Employment": <FileText size={14} />,
  };

  const categories = useMemo(() => {
    const cats = [...new Set(posts.map((p) => p.category))];
    return cats.sort();
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (selectedCategory === "all") return posts;
    return posts.filter((p) => p.category === selectedCategory);
  }, [posts, selectedCategory]);

  const sortedPosts = useMemo(() => {
    return [...filteredPosts].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [filteredPosts]);

  return (
    <>
      <div className="container py-8 anim-fade-in">
        <Breadcrumbs />

        <div className="page-header">
          <h1 className="page-title">Personal Finance Blog 2026</h1>
          <p className="page-description">
            In-depth guides on income tax, retirement planning, mortgage strategies, and smart investing — covering the US, UK, Australia, Canada, India, Singapore, and more. Updated for 2026.
          </p>
        </div>

        <div className="category-filters-scroll">
          <div className="category-filters">
            <button
              className={`filter-btn ${selectedCategory === "all" ? "active-filter" : ""}`}
              onClick={() => setSelectedCategory("all")}
            >
              <span className="filter-icon"><FolderOpen size={14} /></span>
              <span className="filter-name">All</span>
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`filter-btn ${selectedCategory === cat ? "active-filter" : ""}`}
                onClick={() => setSelectedCategory(cat)}
              >
                <span className="filter-icon">{CATEGORY_ICONS[cat]}</span>
                <span className="filter-name">{cat}</span>
              </button>
            ))}
          </div>
        </div>



        <p className="result-count">
          {sortedPosts.length} {sortedPosts.length === 1 ? "blog" : "blogs"} found
          {selectedCategory !== "all" && <> &middot; <strong>{selectedCategory}</strong></>}
        </p>

        {/* h2 keeps the outline intact: BlogCard titles are h3s, which sat
            directly under the page h1 (a level skip). */}
        <h2 className="sr-only">All articles</h2>
        <div className="blog-grid">
          {sortedPosts.map((post, index) => (
            <BlogCard key={post.slug} post={post} showReadMore={false} priority={index < 2} />
          ))}
        </div>

        {sortedPosts.length === 0 && (
          <div className="empty-state">
            <p>No posts found in <strong>{selectedCategory}</strong>.</p>
          </div>
        )}

        {/* Editorial Introduction Section at the bottom */}
        <div className="blog-editorial-hero">
          <div className="editorial-left-panel">
            <h2 className="editorial-heading">
              <Star size={16} className="star-icon" /> Editorial Vision &amp; Research Methodology
            </h2>
            <p className="editorial-paragraph">
              Welcome to the FinanceLives Personal Finance Publication. In an era of generic financial advice and auto-generated lists, we believe that personal finance is inherently local. A tax reform, a pension cap increase, or a mortgage stress-test change in one country is completely distinct from the rules of another. 
            </p>
            <p className="editorial-paragraph">
              Our editorial mission is to dissect complex, primary-source regulatory documents (such as IRS bulletins, HMRC guidelines, and ATO rulings) and translate them into clear, actionable, mathematical calculations. Every article is designed to accompany our localized simulator suite, helping you model real-world scenarios.
            </p>
          </div>
          <div className="editorial-right-panel">
            <h3 className="commitments-heading">Our Core Commitments:</h3>
            <ul className="commitments-list">
              <li><strong>Primary-Source Authority:</strong> We cite and link directly to official government bodies and tax regulators for all regulatory thresholds.</li>
              <li><strong>Continuous Updates:</strong> Our articles are reviewed and refreshed dynamically following autumn budgets, spring statements, and tax-year rollovers.</li>
              <li><strong>Zero Bias:</strong> We are fully independent and do not receive commissions to recommend specific financial products or investments.</li>
            </ul>
            <div className="editorial-author-row">
              <Link href="/authors/galvin-mendonca" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', width: '100%', textDecoration: 'none' }}>
                <div className="author-avatar-wrapper">
                  <Image src="/author-64.png" width={40} height={40} alt={SITE_AUTHOR.name} />
                </div>
                <div className="author-info">
                  <div className="author-name">{SITE_AUTHOR.name}</div>
                  <div className="author-title">Founder &amp; Lead Researcher</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .blog-editorial-hero {
          display: grid;
          grid-template-columns: 1.25fr 0.75fr;
          gap: var(--space-8);
          padding: var(--space-6);
          margin-top: var(--space-12);
          background: linear-gradient(135deg, var(--bg-surface-hover) 0%, var(--primary-a04) 100%);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
        }
        .editorial-heading {
          font-size: var(--text-lg);
          font-weight: var(--fw-bold);
          margin-bottom: var(--space-3);
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: var(--space-2);
        }
        .star-icon {
          color: var(--primary);
        }
        .editorial-paragraph {
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.6;
          margin: 0 0 var(--space-3) 0;
        }
        .editorial-paragraph:last-child {
          margin-bottom: 0;
        }
        .editorial-right-panel {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
          border-left: 1px solid var(--border-color);
          padding-left: var(--space-6);
        }
        .commitments-heading {
          font-size: var(--text-md);
          font-weight: var(--fw-bold);
          margin: 0;
          color: var(--text-primary);
        }
        .commitments-list {
          margin: 0;
          padding-left: var(--space-4);
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.5;
        }
        .editorial-author-row {
          margin-top: auto;
          padding-top: var(--space-3);
          border-top: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }
        .author-avatar-wrapper {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid var(--border-color);
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .author-info {
          display: flex;
          flex-direction: column;
        }
        .author-name {
          font-size: var(--text-sm);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          line-height: 1.2;
        }
        .author-title {
          font-size: var(--text-2xs);
          color: var(--text-muted);
          line-height: 1.2;
        }
        .editorial-author-row :global(a) {
          transition: transform 0.2s ease;
        }
        .editorial-author-row :global(a):hover {
          transform: translateX(4px);
        }
        .editorial-author-row :global(a):hover .author-name {
          color: var(--primary);
        }
        @media (max-width: 768px) {
          .blog-editorial-hero {
            grid-template-columns: 1fr;
            gap: var(--space-6);
            padding: var(--space-5);
            margin-bottom: var(--space-6);
          }
          .editorial-right-panel {
            border-left: none;
            border-top: 1px solid var(--border-color);
            padding-left: 0;
            padding-top: var(--space-5);
          }
        }
        /* .page-header / .page-title / .page-description use the global
           design-system classes for a hierarchy consistent with every page. */
        .category-filters-scroll {
          overflow-x: auto;
          flex: 1;
          min-width: 0;
          padding-bottom: var(--space-1);
        }
        .category-filters-scroll::-webkit-scrollbar {
          height: 3px;
        }
        .category-filters-scroll::-webkit-scrollbar-thumb {
          background-color: var(--border-color);
          border-radius: var(--radius-full);
        }
        .category-filters {
          display: flex;
          gap: var(--space-2);
          width: max-content;
        }
        .filter-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
        }
        .filter-btn {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-full);
          padding: 6px 14px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: background-color 0.2s, border-color 0.2s, color 0.2s, box-shadow 0.2s;
          font-family: var(--font-body);
          font-size: var(--text-xs);
          font-weight: var(--fw-semibold);
          white-space: nowrap;
        }
        .filter-btn:hover {
          background-color: var(--bg-elevated);
          border-color: var(--border-color-hover);
          color: var(--text-primary);
        }
        .active-filter {
          background-color: var(--info-light);
          border-color: var(--info);
          color: var(--info-fg);
        }

        .result-count {
          font-size: var(--text-xs);
          color: var(--text-muted);
          margin-bottom: var(--space-3);
          font-weight: var(--fw-medium);
        }

        .empty-state {
          text-align: center;
          padding: var(--space-12) 0;
          color: var(--text-muted);
          font-size: var(--text-base);
        }
      `}</style>
    </>
  );
}
